import * as React from 'react';
import './homeWorkspaceModal.scss';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons } from 'rcd-cpp-ui/ui/button/Buttons';
import { Cancel, Primary } from 'rcd-cpp-ui/ui/button';
import { useSelector } from '../../utils/useSelector';
import { AppRefId } from '../../utils/constants';
import { useActions } from './homeWorkspaceModalActions';
import { ModalGroup, ModalGroups } from '../ModalGroup/modalGroup';
import { Typeahead } from '../Typeahead/typeahead';
import { Workspace } from '../../types/Metadata';
import { useAlert } from '../Alert/alert';
import { useLoader } from '../Loader/loader';
import { Popover } from 'rcd-cpp-ui/ui/popover';
import TooltipPopover from '../Popover/popover';

type Props = {
  onSave?: (w: Workspace) => void;
};

export const HomeWorkspaceModal = ({ onSave }: Props) => {
  const userSettings = useSelector(s => s.planning?.userSettings);
  const homeWorkspaceId = useSelector(s => {
    if (!s.planning?.userSettings) return null;
    const userSetting = s.planning.userSettings[AppRefId];
    return userSetting?.props?.homeWorkspaceId;
  });
  const showModal = useSelector(s => s.planning?.showHomeWorkspaceModal);
  const workspaces = useSelector(s => s.planning?.workspaces);
  const [selected, setSelected] = React.useState<Workspace>(null);
  const { closeModal, setHomeWorkspace, loading, Loader, Alert } = useLoader(
    useAlert(useActions())
  );
  React.useEffect(() => {
    if (!selected) {
      const workspace = workspaces?.filter(
        w => w.props.refId == homeWorkspaceId
      )[0];
      if (workspace) setSelected(workspace);
    }
  }, [homeWorkspaceId, workspaces]);

  if (!userSettings) return null;
  if (!!homeWorkspaceId && !showModal) return null;

  return (
    <Modal
      id="homeWSModal"
      header="Set Home Workspace"
      onClose={!!homeWorkspaceId ? closeModal : null}
      footer={
        <Buttons>
          {!!homeWorkspaceId && <Cancel onClick={closeModal} />}
          <Primary
            label="Save"
            onClick={() => {
              setHomeWorkspace(selected).then(() => {
                if (onSave) onSave(selected);
                else closeModal();
              });
            }}
            disabled={
              loading || !selected || selected?.props?.refId == homeWorkspaceId
            }
          />
        </Buttons>
      }
    >
      <Loader />
      <ModalGroups>
        <ModalGroup>
          <Alert />
          <div>
            <div className="field-container">
              <label>Home Workspace</label>
              <div className="home-workspace-description">
                <TooltipPopover
                  type="info"
                  pointerPosition="right"
                  content={
                    !!homeWorkspaceId
                      ? 'This is a feature to set your home workspace. Whenever you log into the application, this information will be used to direct you to your home workspace rather than the workspace you last visited.'
                      : 'This is a one-time prompt for you to set your home workspace. Whenever you log into the application, this information will be used to direct you to your home workspace rather than the workspace you last visited. You can update your home workspace from your profile icon at any time. '
                  }
                />
              </div>
            </div>

            <Typeahead
              value={selected?.props?.name || ''}
              options={workspaces}
              disabled={false}
              showAllByDefault
              includes={(t, s) => {
                if (selected?.props?.name == t) {
                  return s?.props?.name != t;
                }
                return s?.props?.name
                  .toLocaleLowerCase()
                  .includes(t?.toLowerCase());
              }}
              onSelect={data => setSelected(data)}
              renderOption={o => o.props.name}
            />
          </div>
        </ModalGroup>
      </ModalGroups>
    </Modal>
  );
};
